import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, {
  setAccessToken,
  clearAccessToken,
  refreshApi,
  scheduleProactiveRefresh,
  cancelProactiveRefresh,
} from '../../services/api';
import i18n from '../../i18n';

// ── Thunks ────────────────────────────────────────────────────────────────────

export const loginAsync = createAsyncThunk(
  'auth/loginAsync',
  async ({ username, password }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', { username, password });
      const data = response.data;
      setAccessToken(data.token);
      scheduleProactiveRefresh(data.token, () => dispatch(silentRefreshAsync()));
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Identifiants incorrects'
      );
    }
  }
);

// Restaure la session au démarrage via le cookie HttpOnly
export const initializeAuth = createAsyncThunk(
  'auth/initializeAuth',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await refreshApi.post('/auth/refresh');
      const data = response.data;
      setAccessToken(data.token);
      scheduleProactiveRefresh(data.token, () => dispatch(silentRefreshAsync()));
      return data;
    } catch {
      return rejectWithValue(null); // pas de session → pas d'erreur affichée
    }
  }
);

// Refresh silencieux déclenché 2 min avant expiry (évite les 401 en cours de session)
export const silentRefreshAsync = createAsyncThunk(
  'auth/silentRefresh',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await refreshApi.post('/auth/refresh');
      const data = response.data;
      setAccessToken(data.token);
      scheduleProactiveRefresh(data.token, () => dispatch(silentRefreshAsync()));
      return data;
    } catch {
      clearAccessToken();
      cancelProactiveRefresh();
      window.dispatchEvent(new CustomEvent('auth:expired'));
      return rejectWithValue(null);
    }
  }
);

export const changePasswordAsync = createAsyncThunk(
  'auth/changePassword',
  async ({ currentPassword, newPassword, confirmNewPassword }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/change-password', {
        currentPassword,
        newPassword,
        confirmNewPassword,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors du changement de mot de passe'
      );
    }
  }
);

export const logoutAsync = createAsyncThunk(
  'auth/logoutAsync',
  async () => {
    try {
      await refreshApi.post('/auth/logout');
    } catch {
      // Logout best-effort : on vide l'état même si l'appel échoue
    }
  }
);

export const updateUserLangue = createAsyncThunk(
  'auth/updateUserLangue',
  async (langue, { rejectWithValue }) => {
    try {
      await api.patch('/auth/langue', { langue });
      return langue;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors du changement de langue'
      );
    }
  }
);

// ── Helpers ───────────────────────────────────────────────────────────────────

function applyLangue(langue) {
  const lang = langue || 'fr';
  i18n.changeLanguage(lang);
}

function userFromPayload(payload) {
  const { role, tenantId, username, forcePasswordChange, passwordLastChangedAt, pack, langue } = payload;
  return {
    username,
    role,
    tenantId,
    forcePasswordChange: !!forcePasswordChange,
    passwordLastChangedAt: passwordLastChangedAt || null,
    pack: pack || null,
    langue: langue || 'fr',
  };
}

// ── Slice ─────────────────────────────────────────────────────────────────────

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    initializing: true,   // true tant que initializeAuth n'a pas répondu
    loading: false,
    error: null,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      clearAccessToken();
      cancelProactiveRefresh();
    },
    clearAuthError: (state) => { state.error = null; },
    clearForcePasswordChange: (state) => {
      if (state.user) {
        state.user.forcePasswordChange = false;
        state.user.passwordLastChangedAt = new Date().toISOString();
      }
    },
  },
  extraReducers: (builder) => {
    // ── login ──
    builder
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.user = userFromPayload(action.payload);
        applyLangue(action.payload.langue);
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── initializeAuth (restauration de session au démarrage) ──
    builder
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.user = userFromPayload(action.payload);
        state.initializing = false;
        applyLangue(action.payload.langue);
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.user = null;
        state.initializing = false;
      });

    // ── silentRefresh (proactive, invisible pour l'utilisateur) ──
    builder
      .addCase(silentRefreshAsync.fulfilled, (state, action) => {
        state.user = userFromPayload(action.payload);
        // pas d'applyLangue ici : la langue est déjà active, ne pas interrompre l'UX
      })
      .addCase(silentRefreshAsync.rejected, (state) => {
        state.user = null;
      });

    // ── logout ──
    builder
      .addCase(logoutAsync.fulfilled, (state) => {
        state.user = null;
        clearAccessToken();
        cancelProactiveRefresh();
      })
      .addCase(logoutAsync.rejected, (state) => {
        state.user = null;
        clearAccessToken();
        cancelProactiveRefresh();
      });

    // ── updateUserLangue ──
    builder
      .addCase(updateUserLangue.fulfilled, (state, action) => {
        if (state.user) state.user.langue = action.payload;
        applyLangue(action.payload);
      });
  },
});

// ── changePassword extraReducers ──
// (gestion dans la page ChangePassword.jsx via unwrap, pas de state ici)

export const { logout, clearAuthError, clearForcePasswordChange } = authSlice.actions;
export default authSlice.reducer;
